import { Capacitor } from '@capacitor/core';

import type { LynxBluetoothState } from './advertiserDefinitions';
import type { LynxLocationAuthorization } from './locationDefinitions';

/**
 * A sim phone: a browser tab that runs Find the way the iOS app does, with GPS, compass and Bluetooth supplied by a test
 * runner instead of the hardware (scripts/sim, 2026-10-31). Everything above the two native plugins is the real app -
 * the engine, the publisher's rules, Supabase, realtime, the roster - which is where the field bugs have been.
 *
 * Two keys, both needed: a build made with VITE_SIM_PHONE=1 (scripts/sim/run.mjs starts one, pointed at the
 * prii-harness project; production never sets it, so all of this folds away), and a host object the runner puts on
 * window before the app loads.
 */

export type SimFix = {
  lat: number;
  lng: number;
  accuracy: number;
  /** When the fix was taken; the runner backdates it to play a GPS that stopped delivering. */
  timestampMs: number;
  speed?: number;
  heading?: number;
};

export type SimPhoneState = {
  phone: string;
  /** Null: the GPS has nothing (indoors, just launched). */
  fix: SimFix | null;
  headingDeg: number | null;
  locationAuth: LynxLocationAuthorization;
  bluetooth: LynxBluetoothState;
  /**
   * Tokens the publisher refreshed while the page was frozen (the runner played Swift in the pocket). The plugin takes
   * them over on wake, as Swift keeps its own tokens through a background stretch.
   */
  publishTokens?: { accessToken: string; refreshToken: string } | null;
};

export type SimPhoneHost = {
  state: SimPhoneState;
  /** Called after every change the runner makes to state. */
  subscribe(listener: () => void): () => void;
  /** What the native side did (publishes, gates, errors), for the runner's log. */
  report(kind: string, detail: unknown): void;
};

declare global {
  interface Window {
    __priiSim?: SimPhoneHost;
  }
}

const SIM_BUILD = import.meta.env?.VITE_SIM_PHONE === '1';

export function getSimPhoneHost(): SimPhoneHost | null {
  if (!SIM_BUILD || typeof window === 'undefined') return null;
  return window.__priiSim ?? null;
}

/** This tab is a sim phone. Read once: the host is put on window before any app code runs. */
export const SIM_PHONE = getSimPhoneHost() !== null;

/** Where the app asks "is this the iOS app?" to decide whether Find runs at all. */
export const NATIVE_OR_SIM = Capacitor.isNativePlatform() || SIM_PHONE;
